import AuthenticatedUserService, { IAuthenticatedUserService } from '@services/authenticated-user-service';
import ConfigurationValuesService, {IConfigurationValuesService} from '@services/ConfigurationValuesService';
import {DocumentReviewApiService, IDocumentReviewApiService} from '@services/document-review-api-service';
import {DocumentUpdateApiService, IDocumentUpdateApiService} from '@services/document-update-api-service';
import ErrorReportingService, {
  IErrorReportingService
} from '@services/error-reporting-service';
import {createMultiInstanceFactory, createSingleInstanceFactory} from '@services/factory-instances';
import {ApplicationInfoInterceptor, IApplicationInfoInterceptor} from '@services/http/application-info-interceptor';
import HttpService from '@services/HttpService';
import LoaderService, {ILoaderService} from '@services/LoaderService';
import {IObjectUrlService, ObjectUrlService} from '@services/object-url-service';
import {
  DocumentDetailsChangeTrackingService,
  IDocumentDetailsChangeTrackingService
} from '@services/onboarding-review/document-details-change-tracking-service';
import {ESignRequestService, IESignRequestService} from '@services/onboarding-review/e-sign-request-service';
import {
  IOnboardingDetailsRequestService,
  OnboardingDetailsRequestService
} from '@services/onboarding-review/onboarding-details-request-service';
import {
  IPersonPidEncryptionService,
  PersonPidEncryptionService
} from '@services/onboarding-review/person-pid-encryption-service';
import PinCodeService, {IPinCodeService} from '@services/pin-code-service';
import RemoteRepresentativeService, {IRemoteRepresentativeService} from '@services/remote-representative-service';
import {ISectionFlowRoutingService, SectionFlowRoutingService} from '@services/section-flow-routing-service';
import TrackingService, {ITrackingService} from '@services/TrackingService';
import TranslationService, {ITranslationService} from '@services/TranslationService';
import ValidateDocumentDetailService, {
  IValidateDocumentDetailService
} from '@services/validate-document-detail-service';
import {AuthenticationStore, IAuthenticationStore} from '@stores/authentication/authentication-store';
import AuthorisationStore, {IAuthorisationStore} from '@stores/authorisation/authorisation-store';
import PinCodeStore, {IPinCodeStore} from '@stores/authorisation/pin-code-store';
import {DeclineModalStore, IDeclineModalStore} from '@stores/modal/decline-modal-store';
import {IReuploadDocumentModalStore, ReuploadDocumentsModalStore} from '@stores/modal/reupload-documents-modal-store';
import {DocumentReviewStore, IDocumentReviewStore} from '@stores/onboarding-review/document-review-store';
import {
  ISection1CandidateDetailStore,
  Section1CandidateDetailStore
} from '@stores/onboarding-review/section1-candidate-detail-store';
import {
  ISection1CandidateDetailUpdater,
  Section1CandidateDetailUpdater
} from '@stores/onboarding-review/section1-candidate-detail-updater';
import {
  ISection1PreparerTranslatorStore,
  Section1PreparerTranslatorStore
} from '@stores/onboarding-review/section1-preparer-translator-store';
import {
  ISection1PreparerTranslatorUpdater,
  Section1PreparerTranslatorUpdater
} from '@stores/onboarding-review/section1-preparer-translator-updater';
import {
  ISection2CertificationStore,
  Section2CertificationStore
} from '@stores/onboarding-review/section2-certification-store';
import {
  ISection2CertificationUpdater,
  Section2CertificationUpdater
} from '@stores/onboarding-review/section2-certification-updater';
import {ISection2CompleteStore, Section2CompleteStore} from '@stores/onboarding-review/section2-complete-store';
import {
  ISection2DocumentSubmissionStore,
  Section2DocumentSubmissionStore
} from '@stores/onboarding-review/section2-document-submission-store';
import {ISection2DocumentsStore, Section2DocumentsStore} from '@stores/onboarding-review/section2-documents-store';
import {
  ISection2DocumentsUpdater,
  Section2DocumentsUpdater
} from '@stores/onboarding-review/section2-documents-updater';
import {ISection2ReviewStore, Section2ReviewStore} from '@stores/onboarding-review/section2-review-store';
import {ISection2ThankyouStore, Section2ThankyouStore} from '@stores/onboarding-review/section2-thankyou-store';
import RemoteRepresentativeStore, {
  IRemoteRepresentativeStore
} from '@stores/remote-representative/remote-representative-store';
import {IValidationPostEditStore, ValidationPostEditStore} from '@stores/validation/validation-post-edit-store';
import documentNumberLookupTable from '../constants/document-number-lookup';
import {
  ISection2DocumentUploadStore,
  Section2DocumentUploadStore
} from '@stores/onboarding-review/section2-document-upload-store';
import {DocumentUploadApiService, IDocumentUploadApiService} from '@services/document-upload-api-service';
import {
  DocumentUploadValidationApiService,
  IDocumentUploadValidationApiService
} from '@services/document-upload-validation-api-service';

export function createPinCodeService(): IPinCodeService {
  const httpService = new HttpService();
  const configurationValuesService = createConfigurationValuesService();

  return new PinCodeService(
    httpService,
    configurationValuesService
  );
}

export function createRemoteRepresentativeService(): IRemoteRepresentativeService {
  const httpService = new HttpService();
  const configurationValuesService = createConfigurationValuesService();

  return new RemoteRepresentativeService(
    httpService,
    configurationValuesService
  );
}

export function createPinCodeStore(): IPinCodeStore {
  const pinCodeService = createPinCodeService();

  return new PinCodeStore(pinCodeService);
}

export function createAuthenticationStore(): IAuthenticationStore {
  const pinCodeService = createPinCodeService();
  const authorisationStore = createAuthorisationStore();

  return new AuthenticationStore(
    authorisationStore,
    pinCodeService
  );
}

export function createRemoteRepresentativeStore(): IRemoteRepresentativeStore {
  const remoteRepresentativeService = createRemoteRepresentativeService();
  const configurationValuesService = ConfigurationValuesService.getInstance();
  const trackingService = createTrackingService();
  const declineModalStore = createDeclineModalStore();

  return new RemoteRepresentativeStore(
    remoteRepresentativeService,
    configurationValuesService,
    trackingService,
    declineModalStore
  );
}

export function createTrackingService(): ITrackingService {
  return new TrackingService();
}

export function createDocumentReviewApiService(): IDocumentReviewApiService {
  const httpService = new HttpService();
  const configurationValuesService = createConfigurationValuesService();

  return new DocumentReviewApiService(
    httpService,
    configurationValuesService,
    createObjectUrlService(),
    createErrorReportingService()
  );
}

export function createDocumentUploadApiService(): IDocumentUploadApiService {
  const httpService = new HttpService();
  const configurationValuesService = createConfigurationValuesService();

  return new DocumentUploadApiService(
    httpService,
    configurationValuesService
  );
}

export function createDocumentUploadValidationApiService(): IDocumentUploadValidationApiService {
  const httpService = new HttpService();
  const configurationValuesService = createConfigurationValuesService();

  return new DocumentUploadValidationApiService(
      httpService,
      configurationValuesService
  );
}

export function createObjectUrlService(): IObjectUrlService {
  return new ObjectUrlService();
}

export function createDocumentReviewStore(): IDocumentReviewStore {
  return new DocumentReviewStore(
    createDocumentReviewApiService(),
    createObjectUrlService(),
    createConfigurationValuesService()
  );
}

export function createDeclineModalStore(): IDeclineModalStore {
  return new DeclineModalStore(
    createRemoteRepresentativeService()
  );
}

export function createReuploadDocumentsModalStore(): IReuploadDocumentModalStore {
  const factoryMethod = createMultiInstanceFactory(
    () => new ReuploadDocumentsModalStore(
      createRemoteRepresentativeService()
    )
  );
  return factoryMethod();
}

export function createPersonPidEncryptionService(): IPersonPidEncryptionService {
  const factoryMethod = createMultiInstanceFactory(
    () => new PersonPidEncryptionService(
      createConfigurationValuesService()
    )
  );

  return factoryMethod();
}

export function createSection1CandidateDetailUpdater(): ISection1CandidateDetailUpdater {
  const factoryMethod = createMultiInstanceFactory(
    () => new Section1CandidateDetailUpdater(
      createSection1CandidateDetailsStore(),
      createPersonPidEncryptionService(),
      createOnboardingDetailsRequestService()
    )
  );

  return factoryMethod();
}

export function createSection1PreparerTranslatorUpdater(): ISection1PreparerTranslatorUpdater {
  const factoryMethod = createMultiInstanceFactory(
    () => new Section1PreparerTranslatorUpdater(
      createSection1PreparerTranslatorStore(),
      createOnboardingDetailsRequestService()
    )
  );

  return factoryMethod();
}

export function createSection2CertificationUpdater(): ISection2CertificationUpdater {
  const factoryMethod = createMultiInstanceFactory(
    () => new Section2CertificationUpdater(
      createSection2CertificationStore(),
      createOnboardingDetailsRequestService()
    )
  );

  return factoryMethod();
}

export function createESignRequestService(): IESignRequestService {
  const factoryMethod = createMultiInstanceFactory(
    () => new ESignRequestService(
      createSection2CompleteStore(),
      createSection2CertificationStore(),
      createRemoteRepresentativeService()
    )
  );

  return factoryMethod();
}

export function createSection2DocumentsUpdater(): ISection2DocumentsUpdater {
  const factoryMethod = createMultiInstanceFactory(
    () => new Section2DocumentsUpdater(
      createSection2DocumentsStore(),
      createOnboardingDetailsRequestService(),
      createPersonPidEncryptionService(),
      createConfigurationValuesService()
    )
  );

  return factoryMethod();
}

export function createSection2ThankyouStore(): ISection2ThankyouStore {
  const factoryMethod = createMultiInstanceFactory(
    () => new Section2ThankyouStore(
      createRemoteRepresentativeService()
    )
  );

  return factoryMethod();
}

export function createSectionFlowRoutingService(): ISectionFlowRoutingService {
  const factoryMethod = createMultiInstanceFactory(
    () => new SectionFlowRoutingService(
      createRemoteRepresentativeService(),
      createConfigurationValuesService()
    )
  );

  return factoryMethod();
}

export function createDocumentUpdateApiService(): IDocumentUpdateApiService {
  const factoryMethod = createMultiInstanceFactory(
    () => new DocumentUpdateApiService(
      createConfigurationValuesService(),
      new HttpService()
    )
  );

  return factoryMethod();
}

export function createValidateDocumentDetailService(): IValidateDocumentDetailService {
  const factoryMethod = createMultiInstanceFactory(
    () => new ValidateDocumentDetailService(
      documentNumberLookupTable,
      createSection1CandidateDetailsStore(),
      createConfigurationValuesService()
    )
  );

  return factoryMethod();
}

export function createValidationPostEditStore(): IValidationPostEditStore {
  const factoryMethod = createMultiInstanceFactory(
      () => new ValidationPostEditStore()
  );

  return factoryMethod();
}

export function createErrorReportingService(): IErrorReportingService {
  const factory = createMultiInstanceFactory(
      () => new ErrorReportingService()
  );

  return factory();
}

export function createAuthenticatedUserService(): IAuthenticatedUserService {
  const factory = createMultiInstanceFactory(
      () => new AuthenticatedUserService(
          createRemoteRepresentativeService(),
          createAuthorisationStore(),
          createErrorReportingService()
      )
  );

  return factory();
}

/**
 * Single instance factories
 */

let authorisationStoreInstance: IAuthorisationStore | null = null;

export function createAuthorisationStore(): IAuthorisationStore {
  if (authorisationStoreInstance === null) {
    authorisationStoreInstance = new AuthorisationStore();
  }

  return authorisationStoreInstance;
}

export function createSection2CompleteStore(): ISection2CompleteStore {
  const factoryMethod = createSingleInstanceFactory(
    () => new Section2CompleteStore(
      createConfigurationValuesService()
    ),
    Section2CompleteStore
  );

  return factoryMethod();
}

export function createSection2DocumentsStore(): ISection2DocumentsStore {
  const factoryMethod = createSingleInstanceFactory(
    () => new Section2DocumentsStore(
      createConfigurationValuesService()
    ),
    Section2DocumentsStore
  );

  return factoryMethod();
}

export function createSection2CertificationStore(): ISection2CertificationStore {
  const factoryMethod = createSingleInstanceFactory(
    () => new Section2CertificationStore(
      createConfigurationValuesService()
    ),
    Section2CertificationStore
  );

  return factoryMethod();
}

/**
 * Single instance as the data needs to be shared over multiple pages in Section 2
 * for the purposes of auditing
 */
export function createSection1CandidateDetailsStore(): ISection1CandidateDetailStore {
  const factoryMethod = createSingleInstanceFactory(
    () => new Section1CandidateDetailStore(),
    Section1CandidateDetailStore
  );
  return factoryMethod();
}

/**
 * Single instance as the data needs to be shared over multiple pages in Section 2
 * for the purposes of auditing
 */
export function createSection1PreparerTranslatorStore(): ISection1PreparerTranslatorStore {
  const factoryMethod = createSingleInstanceFactory(
    () => new Section1PreparerTranslatorStore(),
    Section1PreparerTranslatorStore
  );
  return factoryMethod();
}

/**
 * Temporarily a single instance as the request events live here for now
 * until a better spot can be designed
 */
export function createOnboardingDetailsRequestService(): IOnboardingDetailsRequestService {
  const factoryMethod = createSingleInstanceFactory(
    () => new OnboardingDetailsRequestService(
      createRemoteRepresentativeService()
    ),
    OnboardingDetailsRequestService
  );

  return factoryMethod();
}

/**
 * Single instance as the data needs to be shared over multiple pages in Section 2
 * for the purposes of auditing
 */
export function createSection2ReviewStore(): ISection2ReviewStore {
  const factoryMethod = createSingleInstanceFactory(
    () => new Section2ReviewStore(),
    Section2ReviewStore
  );

  return factoryMethod();
}

export function createSection2DocumentUploadStore(): ISection2DocumentUploadStore {
  return new Section2DocumentUploadStore(
    createDocumentUploadApiService(),
    createRemoteRepresentativeService(),
    createDocumentUploadValidationApiService(),
    createPersonPidEncryptionService(),
    createDocumentUpdateApiService(),
  );
}

/**
 * Single instance as the instance that is initiated in App,
 * responds to the request event in employee information page
 * and the instance in Complete I9 page need to be the same
 */
export function createDocumentDetailsChangeTrackingService(): IDocumentDetailsChangeTrackingService {
  const factoryMethod = createSingleInstanceFactory(
    () => new DocumentDetailsChangeTrackingService(
      createSection2DocumentsStore(),
      createOnboardingDetailsRequestService(),
      createPersonPidEncryptionService(),
      createDocumentUpdateApiService(),
      createValidateDocumentDetailService(),
      createSection2DocumentSubmissionStore()
    ),
    DocumentDetailsChangeTrackingService
  );

  return factoryMethod();
}

export function createSection2DocumentSubmissionStore(): ISection2DocumentSubmissionStore {
  const factoryMethod = createSingleInstanceFactory(
      () => new Section2DocumentSubmissionStore(),
      Section2DocumentSubmissionStore
  );

  return factoryMethod();
}

/**
 * Single instance as having more than one in the interceptor chain would be
 * unnecessary
 */
export function createApplicationInfoInterceptor(): IApplicationInfoInterceptor {
  const factoryMethod = createSingleInstanceFactory(
    () => new ApplicationInfoInterceptor(
      createConfigurationValuesService()
    ),
    ApplicationInfoInterceptor
  );

  return factoryMethod();
}

/**
 * Legacy factories for old style singleton services
 */

export function createLoaderService(): ILoaderService {
  return LoaderService.getInstance();
}

export function createConfigurationValuesService(): IConfigurationValuesService {
  return ConfigurationValuesService.getInstance();
}

export function createTranslationService(): ITranslationService {
  return TranslationService.getInstance();
}



// WEBPACK FOOTER //
// ./src/services/factories.ts